/**
 * Centralized Country Configuration
 * 
 * This file contains the canonical list of countries used throughout the application.
 * All country dropdowns should use this configuration.
 * 
 * Structure: { value: ISO_CODE, labelKey: 'i18n.key' }
 * 
 * NEVER hardcode country names in components!
 */

export interface CountryOption {
  value: string  // ISO country code (canonical value)
  labelKey: string  // Translation key for display
}

/**
 * Primary countries for the application
 */
export const PRIMARY_COUNTRIES: CountryOption[] = [
  { value: 'DE', labelKey: 'countries.germany' },
  { value: 'AT', labelKey: 'countries.austria' },
  { value: 'FR', labelKey: 'countries.france' },
  { value: 'CH', labelKey: 'countries.switzerland' }
]

/**
 * Extended European countries
 */
export const EUROPEAN_COUNTRIES: CountryOption[] = [
  ...PRIMARY_COUNTRIES,
  { value: 'BE', labelKey: 'countries.belgium' },
  { value: 'ES', labelKey: 'countries.spain' },
  { value: 'IT', labelKey: 'countries.italy' },
  { value: 'NL', labelKey: 'countries.netherlands' },
  { value: 'PL', labelKey: 'countries.poland' },
  { value: 'GB', labelKey: 'countries.unitedKingdom' },
  { value: 'IE', labelKey: 'countries.ireland' },
  { value: 'PT', labelKey: 'countries.portugal' },
  { value: 'SE', labelKey: 'countries.sweden' },
  { value: 'DK', labelKey: 'countries.denmark' },
  { value: 'NO', labelKey: 'countries.norway' },
  { value: 'FI', labelKey: 'countries.finland' }
]

/**
 * All countries (global list)
 */
export const ALL_COUNTRIES: CountryOption[] = [
  ...EUROPEAN_COUNTRIES,
  { value: 'US', labelKey: 'countries.unitedStates' },
  { value: 'CA', labelKey: 'countries.canada' },
  { value: 'AU', labelKey: 'countries.australia' },
  { value: 'NZ', labelKey: 'countries.newZealand' },
  { value: 'JP', labelKey: 'countries.japan' },
  { value: 'CN', labelKey: 'countries.china' },
  { value: 'BR', labelKey: 'countries.brazil' },
  { value: 'MX', labelKey: 'countries.mexico' },
  { value: 'IN', labelKey: 'countries.india' }
]

/**
 * Get country label key by ISO code
 */
export function getCountryLabelKey(countryCode: string): string {
  const country = ALL_COUNTRIES.find(c => c.value === countryCode)
  return country?.labelKey || 'countries.unknown'
}

/**
 * Get country display name using translation function
 */
export function getCountryName(countryCode: string, t: (key: string) => string): string {
  return t(getCountryLabelKey(countryCode))
}

/**
 * Phone country codes with their dial codes
 */
export interface PhoneCountryOption {
  value: string  // ISO country code
  dialCode: string  // International dial code
  labelKey: string  // Translation key
}

export const PHONE_COUNTRIES: PhoneCountryOption[] = [
  { value: 'DE', dialCode: '+49', labelKey: 'countries.germany' },
  { value: 'AT', dialCode: '+43', labelKey: 'countries.austria' },
  { value: 'FR', dialCode: '+33', labelKey: 'countries.france' },
  { value: 'CH', dialCode: '+41', labelKey: 'countries.switzerland' },
  { value: 'BE', dialCode: '+32', labelKey: 'countries.belgium' },
  { value: 'ES', dialCode: '+34', labelKey: 'countries.spain' },
  { value: 'IT', dialCode: '+39', labelKey: 'countries.italy' },
  { value: 'NL', dialCode: '+31', labelKey: 'countries.netherlands' },
  { value: 'GB', dialCode: '+44', labelKey: 'countries.unitedKingdom' },
  { value: 'US', dialCode: '+1', labelKey: 'countries.unitedStates' }
]
